import { Component, OnDestroy, OnInit } from '@angular/core';
import { DialogService } from '../../../../services/dialog.service';
import { BehaviorSubject, defer, EMPTY, of, Subscription } from 'rxjs';
import {
  arr_count,
  arr_first,
  arr_map,
  blank,
  data_get,
  data_has,
  data_keys,
  data_nullify,
  data_omit,
  fallback,
  filled,
  observe,
  OptionalValue,
  spf,
  transform
} from '../../../../shared/utils/common';
import { ApiService } from '../../../../services/api.service';
import { TranslateService } from '@ngx-translate/core';
import { FieldmagicDropdownIdentifiableOption } from '../../../../shared/components/forms/input/dropdown/dropdown.component';
import { PaginationStore } from '../../../../shared/store/pagination.store';
import { filter, switchMap } from 'rxjs/operators';
import { NotificationService } from '../../../../services/notification.service';

@Component({
  selector: 'asset-types-import-from-templates-button',
  template: `
    <fieldmagic-primary-button
      [isInProgress]="importing$ | async"
      icon="import"
      tooltip="Import From Templates"
      purpose="list-action"
      (click)="onClick()"
    >
    </fieldmagic-primary-button>
  `,
  providers: [
    DialogService,
  ],
})
export class AssetTypesImportFromTemplatesButtonComponent {
  readonly importing$ = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly _dialog: DialogService,
  ) { }

  onClick = () => observe({
    before: () => this.importing$.next(true),
    after: () => this.importing$.next(false),
    observable: () => this._dialog.show$({
      component: AssetTypesImportFromTemplatesDialogComponent,
    }),
  }).subscribe();
}

@Component({
  selector: 'asset-types-import-from-templates-dialog',
  template: `
    <fieldmagic-dialog
      icon="import"
      label="Import Asset Types"
      [instance]="this"
    >
      <ng-template fieldmagicDialogButtonsTmp>
        <fieldmagic-primary-button
          [disabled]="initializing$ | async"
          label="Import"
          icon="import"
          (click)="onImport()"
        >
        </fieldmagic-primary-button>
      </ng-template>

      <ng-template fieldmagicDialogContentTmp>
        <div
          *ngIf="(initializing$ | async) || (importing$ | async)"
          class="d-flex d-flex-gap flex-column align-items-center justify-content-center w-100"
        >
          <fieldmagic-icon [spin]="true"></fieldmagic-icon>
          <fieldmagic-text [content]="progress$ | async | translate"></fieldmagic-text>
        </div>

        <div
          *ngIf="(! (initializing$ | async) && ! (importing$ | async))"
          class="d-flex d-flex-gap flex-column w-100"
        >
          <fieldmagic-dropdown-input
            [(ngModel)]="template"
            label="Please select a standard template to use"
            placeholder="Please select a standard template to use"
            [options]="templates$ | async"
            [withRequiredMarker]="true"
            (ngModelChange)="onTemplateChange()"
          >
          </fieldmagic-dropdown-input>

          <ng-container *ngIf="template | filled">
            <table class="table table-bordered">
              <thead>
                <tr>
                  <td>
                    <fieldmagic-text
                      purpose="table-header"
                      content="Asset Type"
                    >
                    </fieldmagic-text>
                  </td>
                  <td
                    *ngIf="(! (fetching$ | async)) && (assetTypes$ | async | filled)"
                  >
                    <fieldmagic-checkbox-input
                      [(ngModel)]="all"
                      [useNative]="true"
                      tooltip="Click to select/unselect all"
                      (ngModelChange)="onSelectAll($event)"
                    >
                    </fieldmagic-checkbox-input>
                  </td>
                </tr>
              </thead>

              <tbody>
                <ng-container *ngIf="fetching$ | async">
                  <tr class="text-center">
                    <td colspan="2">
                      <fieldmagic-icon
                        [spin]="true"
                        position="center"
                      >
                      </fieldmagic-icon>
                    </td>
                  </tr>
                </ng-container>

                <ng-container *ngIf="! (fetching$ | async)">
                  <tr *ngIf="assetTypes$ | async | blank">
                    <td
                      class="text-center"
                      colspan="2"
                    >
                      {{ 'No asset types to import. Please select a different template.' | translate }}
                    </td>
                  </tr>
                  <tr *ngFor="let asset_type of (assetTypes$ | async); let asset_type_index = index;">
                    <td>{{ asset_type.name }}</td>
                    <td width="2%">
                      <fieldmagic-checkbox-input
                        [(ngModel)]="asset_type.included"
                        [useNative]="true"
                        tooltip="Click to select/unselect"
                        (ngModelChange)="onSelect($event, asset_type_index)"
                      >
                      </fieldmagic-checkbox-input>
                    </td>
                  </tr>
                </ng-container>
              </tbody>
            </table>
          </ng-container>
        </div>
      </ng-template>
    </fieldmagic-dialog>
  `,
  providers: [
    PaginationStore,
  ]
})
export class AssetTypesImportFromTemplatesDialogComponent implements OnInit, OnDestroy {
  all: boolean = true;
  template?: OptionalValue<AssetTypeImportTemplateOption>;

  readonly initializing$ = new BehaviorSubject<boolean>(true);
  readonly importing$ = new BehaviorSubject<boolean>(false);
  readonly progress$ = new BehaviorSubject<OptionalValue<string>>(null);

  readonly templates$ = new BehaviorSubject<AssetTypeImportTemplateOption[]>([]);

  readonly fetching$ = new BehaviorSubject<boolean>(false);
  readonly assetTypes$ = new BehaviorSubject<AvailableAssetType[]>([]);

  private _shouldExclude: boolean = true;
  private _selected: Record<string, string> = {};

  private _template$ = new BehaviorSubject<OptionalValue<AssetTypeImportTemplateOption>>(null);

  private _onTemplateFetchingSubscription?: Subscription;
  private _onTemplateChangeSubscription?: Subscription;
  private _onListChangeSubscription?: Subscription;

  constructor(
    private readonly _api: ApiService,
    private readonly _translator: TranslateService,
    private readonly _notifications: NotificationService,
  ) { }

  ngOnInit(): void {
    this.progress$.next(this._translator.instant('Initializing....'));

    this._onTemplateFetchingSubscription = observe({
      after: () => this.initializing$.next(false),
      observable: () => this._api.callPrivate$({
        path: '/asset_types/get_standard_templates',
        expectsNewFormat: true,
        onSuccessResponse: (response) => response,
      })
    }).subscribe((response) => {
      const standards = fallback(response.get('standards'), {
        to: () => [],
      });
      const templates = arr_map(standards, {
        transformer: (standard) => ({
          id: standard.id,
          text: standard.text,
          encoded_name: standard.encoded_name,
        }),
      });

      this.templates$.next(templates);

      if (arr_count(templates) === 1) {
        this.template = arr_first(templates);
        this._template$.next(this.template);
      }
    });

    this._onTemplateChangeSubscription = this._template$.pipe(
      filter((template) => filled(template)),
      switchMap((template) => observe({
        before: () => this.fetching$.next(true),
        after: () => this.fetching$.next(false),
        observable: () => this._api.callPrivate$({
          path: '/asset_types/get_for_template',
          expectsNewFormat: true,
          body: {
            'standard_template': data_get(template, 'id'),
          },
          onSuccessResponse: (response) => response.get('items'),
        }),
      }))
    ).subscribe((assetTypes) => this.assetTypes$.next(arr_map(assetTypes, {
      transformer: (assetType) => ({
        ...assetType,
        included: (this._shouldExclude && !data_has(this._selected, assetType.id))
          || (!this._shouldExclude && data_has(this._selected, assetType.id)),
      })
    })));
  }

  ngOnDestroy(): void {
    if (this._onTemplateFetchingSubscription) {
      this._onTemplateFetchingSubscription.unsubscribe();
    }

    if (this._onTemplateChangeSubscription) {
      this._onTemplateChangeSubscription.unsubscribe();
    }

    if (this._onListChangeSubscription) {
      this._onListChangeSubscription.unsubscribe();
    }
  }

  onTemplateChange = () => this._template$.next(this.template);

  onSelectAll(checked: boolean): void {
    this._shouldExclude = checked;
    this._selected = {};

    this.assetTypes$.next([...arr_map(this.assetTypes$.value, {
      transformer: (assetType) => ({
        ...assetType,
        included: checked,
      }),
    })]);
  }

  onSelect(checked: boolean, index: number): void {
    if (index < 0) {
      return;
    }

    const assetTypes = [... this.assetTypes$.value];
    const assetType = assetTypes[index];

    if (!data_has(this._selected, assetType.id)) {
      this._selected[assetType.id] = assetType.id;
    } else {
      this._selected = data_omit(this._selected, {
        keys: assetType.id,
      });
    }

    this.all = checked && blank(this._selected);
  }

  onImport = () => observe({
    before: () => this.importing$.next(true),
    after: () => this.importing$.next(false),
    observable: () => defer(() => {
      if (!this._shouldExclude && blank(this._selected)) {
        this._notifications.notifyError('Please select atleast 1 asset type to import');
        return EMPTY;
      }

      if (blank(this.assetTypes$.value)) {
        this._notifications.notifyError('No asset types to import.');
        return EMPTY;
      }

      this.progress$.next(spf('Importing %s...', {
        args: [this._translator.instant(this._getTableLabel('asset_types'))],
      }));

      return this._import();
    })
  }).subscribe(() => this._notifications.notifySuccess('Imported successfully.'));

  private _import(args: {
    state?: string;
  } = {}) {
    const ids = transform(this._selected, {
      default_value: [],
      transformer: (selections) => data_keys(selections),
    });

    return this._api.callPrivate$({
      path: '/asset_types/import_from_standard_template',
      expectsNewFormat: true,
      body: {
        standard_template: data_get(this.template, 'id'),
        standard_template_name: data_get(this.template, 'encoded_name'),
        should_exclude_ids: this._shouldExclude,
        ... (filled(data_nullify(args.state)) && {
          next_page: args.state,
        }),
        ... (filled(ids) && {
          ids,
        }),
      },
      onSuccessResponse: (response) => ({
        state: response.get('next_page'),
        next_table: response.get('next_table'),
      }),
    }).pipe(
      switchMap((data) => {
        if (blank(data.state)) {
          return of(null);
        }

        this.progress$.next(spf(this._translator.instant('Importing %s...'), {
          args: [this._translator.instant(this._getTableLabel(data.next_table))],
        }));

        return this._import({
          state: data.state,
        });
      }),
    );
  }

  private _getTableLabel(table: string): string {
    if (table === 'asset_types') {
      return 'Asset Types';
    }

    if (table === 'asset_type_variants') {
      return 'Variants';
    }

    if (table === 'asset_type_possible_faults') {
      return 'Defects';
    }

    if (table === 'asset_type_inspection_checklists') {
      return 'Inspection Checklists';
    }

    return 'Unknown';
  }
}

export type AssetTypeImportTemplateOption = FieldmagicDropdownIdentifiableOption<{
  encoded_name: string;
}>;

export type AvailableAssetType = {
  id: string;
  name: string;
  included?: boolean;
}